import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Mezopitlogo from "./mezopitnewlogo.png";
import './navbar.css';
import './mobilenavbar.css';
import {
  FaPhoneAlt,
  FaGift,
  FaChevronDown,
  FaChessKnight,
  FaNetworkWired,
  FaCode,
  FaMicrosoft,
  FaUserTie,
  FaNewspaper,
  FaBriefcase,
  FaEnvelopeOpenText,
  FaServer,
  FaDatabase,
  FaChartBar,
  FaMicrochip,
  FaChalkboardTeacher,
  FaShieldAlt,
  FaRobot,
  FaCogs,
  FaMapMarkerAlt,
  FaLinkedin,
  FaInstagram,
  FaWhatsapp,
  FaYoutube 
} from 'react-icons/fa';
import { FaXTwitter } from "react-icons/fa6";

const TurkishFlag = () => (
  <svg width="20" height="15" viewBox="0 0 30 20" style={{ marginRight: '6px' }}>
    <rect width="30" height="20" fill="#E30A17"/>
    <circle cx="12" cy="10" r="5" fill="white"/>
    <circle cx="14" cy="10" r="4" fill="#E30A17"/>
    <path d="M19 7 L21 10 L19 13 L22 10 Z" fill="white"/>
  </svg>
);

const EnglishFlag = () => (
  <svg width="20" height="15" viewBox="0 0 60 30" style={{ marginRight: '6px' }}>
    <rect width="60" height="30" fill="#012169"/>
    <path d="M0 0 L60 30 M60 0 L0 30" stroke="white" strokeWidth="6"/>
    <path d="M0 0 L60 30 M60 0 L0 30" stroke="#C8102E" strokeWidth="2"/>
    <path d="M30 0 L30 30 M0 15 L60 15" stroke="white" strokeWidth="10"/>
    <path d="M30 0 L30 30 M0 15 L60 15" stroke="#C8102E" strokeWidth="4"/>
  </svg>
);

const Navbar = () => {
  const { t, i18n } = useTranslation(); 
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false); 
  const navbarRef = useRef(null);
  const langDropdownRef = useRef(null);

  const handleScroll = () => setScrolled(window.scrollY > 50);
  const handleResize = () => setIsMobile(window.innerWidth <= 992);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    document.body.classList.add('has-announcement');
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.body.classList.remove('has-announcement');
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setDropdownOpen(null);
    setLangDropdownOpen(false);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangDropdownOpen(false);
    localStorage.setItem('preferredLanguage', lng);
  };

  const languages = [
    { 
      code: 'tr', 
      name: 'Türkçe', 
      flag: <TurkishFlag />,
      shortName: 'TR'
    },
    { 
      code: 'en', 
      name: 'English', 
      flag: <EnglishFlag />,
      shortName: 'EN'
    }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const announcements = t('announcements', { returnObjects: true });

  const serviceCategories = [
    { title: t('strategy_consultant'), icon: <FaChessKnight size={16} />, link: "/strategyandconsultant", description: t('strategy_consultant_desc') },
    { title: t('operation_support'), icon: <FaNetworkWired size={16} />, link: "/operationandsupport", description: t('operation_support_desc') },
    { title: t('software_development'), icon: <FaCode size={16} />, link: "/softwaredevelopment", description: t('software_development_desc') },
    { title: t('other_services'), icon: <FaRobot size={16} />, link: "/yapayzekaveotomasyon", description: t('other_services_desc') },
    { title: t('microsoft_solutions'), icon: <FaMicrosoft size={16} />, link: "/microsoft365solutions", description: t('microsoft_solutions_desc') },
    { title: t('devops_cloud'), icon: <FaServer size={16} />, link: "/devopsandcloud", description: t('devops_cloud_desc') },
    { title: t('corporate_solutions'), icon: <FaDatabase size={16} />, link: "/corporatesolutions", description: t('corporate_solutions_desc') },
    { title: t('data_analytics'), icon: <FaChartBar size={16} />, link: "/dataandanaliticssolutions", description: t('data_analytics_desc') },
    { title: t('iot_solutions'), icon: <FaMicrochip size={16} />, link: "/iotsolutions", description: t('iot_solutions_desc') },
    { title: t('education_hr'), icon: <FaChalkboardTeacher size={16} />, link: "/educationhrsolutions", description: t('education_hr_desc') },
    { title: t('cybersecurity'), icon: <FaShieldAlt size={16} />, link: "/cybersecuritysolutions", description: t('cybersecurity_desc') }
  ];

const menuItems = [
  {
    text: "Hizmetler",
    link: null,
    dropdown: [
      { text: t('all_services'), link: "/hizmet-alanlarimiz", icon: <FaCogs size={16} /> },
      ...serviceCategories
    ]
  },
  { text: t('cozumler'), link: "/cozumlerimiz" },
  { text: t('packages'), link: "/paketlerimiz" },,
  { text: t('projects'), link: "/son-tamamlanan-projelerimiz" },

{
  text: "Şirket",
  link: null,
  dropdown: [
    { 
      text: "Hakkımızda", 
      link: "/hakkimizda", 
      icon: <FaUserTie size={16} /> 
    },
    { 
      text: "Blog", 
      link: "/blog-sayfamiz", 
      icon: <FaNewspaper size={16} /> 
    },
    { 
      text: "Kariyer", 
      link: "/sizin-kariyeriniz", 
      icon: <FaBriefcase size={16} /> 
    },
    { 
      text: "İletişim", 
      link: "/bizimle-iletisime-gecin", 
      icon: <FaEnvelopeOpenText size={16} /> 
    }
  ]
}
];

  const AnnouncementBar = () => {
    return (
      <div className="announcement-bar">
        <div className="announcement-track">
          {announcements.map((message, index) => (
            <span key={`first-${index}`} className="announcement-item">
              {message}
            </span>
          ))}
          {announcements.map((message, index) => (
            <span key={`second-${index}`} className="announcement-item">
              {message}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderMegaDropdown = (items) => (
    <div className="mega-dropdown">
      <div className="mega-dropdown-grid">
        {items.map((item, index) => (
          <div key={index} className="mega-dropdown-item">
            <Link to={item.link} onClick={closeMobileMenu}>
              <div className="mega-dropdown-icon">{item.icon}</div>
              <div className="mega-dropdown-text">
                <h4>{item.text || item.title}</h4>
                {item.description && <p>{item.description}</p>}
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );

  const LanguageSelector = () => (
    <div className="language-selector" ref={langDropdownRef}>
      <button 
        className="language-button"
        onClick={() => setLangDropdownOpen(!langDropdownOpen)}
      >
        <span className="current-language">
          {currentLanguage.flag}
          <span className="language-name">{currentLanguage.shortName}</span>
        </span>
        <FaChevronDown className={`dropdown-arrow ${langDropdownOpen ? 'rotated' : ''}`} />
      </button>
      
      {langDropdownOpen && (
        <div className="language-dropdown">
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={`language-option ${i18n.language === lang.code ? 'active' : ''}`}
              onClick={() => changeLanguage(lang.code)}
            >
              {lang.flag}
              <span className="language-name">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderDesktopNav = () => (
    <div className={`navbar-wrapper ${scrolled ? 'scrolled' : ''}`}>
      <AnnouncementBar />
      <nav className={`navbar desktop ${scrolled ? 'scrolled' : ''}`} ref={navbarRef}>
        <div className="navbar-container">
          <Link to="/" className="navbar-logo" onClick={closeMobileMenu}>
            <img src={Mezopitlogo} alt="Mezopit Logo" />
          </Link>
          
          <ul className="navbar-menu">
            {menuItems.map((item, index) => (
              <li
                key={index}
                className={`navbar-item ${item.dropdown ? 'dropdown' : ''} ${dropdownOpen === index ? 'active' : ''}`}
                onMouseEnter={() => item.dropdown && setDropdownOpen(index)}
                onMouseLeave={() => item.dropdown && setDropdownOpen(null)}
              >
                {item.link ? (
                  <Link to={item.link} onClick={closeMobileMenu}>
                    {item.text}
                  </Link>
                ) : (
                  <span className="dropdown-trigger">
                    {item.text} {item.dropdown && <FaChevronDown className="dropdown-arrow" />}
                  </span>
                )}
                {item.dropdown && dropdownOpen === index && renderMegaDropdown(item.dropdown)}
              </li>
            ))}
          </ul>
          
          <div className="navbar-right">
            <Link to="/bizimle-iletisime-gecin" onClick={closeMobileMenu}>
              <button className="contact-button primary-button">
                <FaPhoneAlt style={{ marginRight: "6px" }} /> {t('contact_us')}
              </button>
            </Link>
            <Link to="/bizimle-iletisime-gecin" onClick={closeMobileMenu}>
              <button className="contact-button secondary-button">
                <FaGift style={{ marginRight: "6px" }} /> {t('get_offer')}
              </button>
            </Link>

            <LanguageSelector />
          </div>
        </div>
      </nav>
    </div>
  );

  const renderMobileNav = () => (
    <div className={`navbar-wrapper has-announcement ${scrolled ? 'scrolled' : ''}`}>
      <AnnouncementBar />
      <nav className={`navbar mobile ${scrolled ? 'scrolled' : ''}`} ref={navbarRef}>
        <div className="navbarmobile-container">
          <Link to="/" className="navbarmobile-logo" onClick={closeMobileMenu}>
            <img src={Mezopitlogo} alt="Mezopit Logo" />
          </Link>
          
          <div className="mobile-right-actions">
            <div className="mobile-language-selector">
              <button 
                className="mobile-language-button"
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
              >
                {currentLanguage.flag}
                <span className="mobile-language-short">{currentLanguage.shortName}</span>
              </button>
              {langDropdownOpen && (
                <div className="mobile-language-dropdown">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`mobile-language-option ${i18n.language === lang.code ? 'active' : ''}`}
                      onClick={() => changeLanguage(lang.code)}
                    >
                      {lang.flag}
                      <span className="language-name">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button 
              className="navbarmobile-toggle" 
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? "Menüyü kapat" : "Menüyü aç"}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>
        
        <div className={`mobile-menu-container ${menuOpen ? 'active' : ''}`}>
          <ul className={`mobile-menu ${menuOpen ? 'active' : ''}`}>
            {menuItems.map((item, index) => (
              <li key={index} className={`mobile-item ${item.dropdown ? 'dropdown' : ''}`}>
                {item.link ? (
                  <Link 
                    to={item.link} 
                    onClick={closeMobileMenu}
                    className="mobile-menu-link"
                  >
                    {item.text}
                  </Link>
                ) : (
                  <span 
                    className="mobile-menu-trigger"
                    onClick={() => toggleDropdown(index)}
                  >
                    {item.text} 
                    {item.dropdown && (
                      <FaChevronDown className={`dropdown-arrow ${dropdownOpen === index ? 'rotated' : ''}`} />
                    )}
                  </span>
                )}
                {item.dropdown && dropdownOpen === index && (
                  <ul className="mobile-dropdown">
                    {item.dropdown.map((sub, subIndex) => (
                      <li key={subIndex}>
                        <Link 
                          to={sub.link} 
                          onClick={closeMobileMenu}
                          className="mobile-dropdown-link"
                        >
                          <span className="dropdown-icon">{sub.icon}</span>
                          <span className="dropdown-text">
                            <strong>{sub.text || sub.title}</strong>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            
            <div className="mobile-contact-info">
              <div className="contact-info-item">
                <FaMapMarkerAlt className="contact-icon" />
                <span>Gaziantep, Türkiye</span>
              </div>
            </div>

            <div className="mobile-right">
              <Link to="/bizimle-iletisime-gecin" onClick={closeMobileMenu}>
                <button className="mobilecontact-button mobileprimary-button">
                  <FaPhoneAlt style={{ marginRight: "6px" }} /> {t('contact_us')}
                </button>
              </Link>
              <Link to="/bizimle-iletisime-gecin" onClick={closeMobileMenu}>
                <button className="mobilecontact-button mobilesecondary-button">
                  <FaGift style={{ marginRight: "6px" }} /> {t('get_offer')}
                </button>
              </Link>
            </div>

            <div className="mobile-nav-footer">
              <div className="mobile-footer-bottom">
                <p>© {t('all_rights_reserved')} <br />2026 MEZOPIT SOFTWARE SOLUTIONS</p>
                <div className="mobile-footer-social-icons">
                  <a href="https://x.com/mezopitsoftion" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                    <FaXTwitter className="social-icon" />
                  </a>
                  <a href="https://www.linkedin.com/company/mezopit-software-solutions" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <FaLinkedin className="social-icon" />
                  </a>
                  <a href="https://www.instagram.com/mezopitsoftware" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <FaInstagram className="social-icon" />
                  </a>
                  <a href="https://wa.me/qr/3EXNW3CQTQNHD1" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                    <FaWhatsapp className="social-icon" />
                  </a>
                  <a href="https://youtube.com/@mezopitsoftwaresolutions?si=JrkkTh2wI0mjyVSn" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                    <FaYoutube className="social-icon" />
                  </a>
                </div>
              </div>
            </div>
          </ul>
        </div>

        {menuOpen && (
          <div 
            className="mobile-menu-overlay" 
            onClick={closeMobileMenu}
          />
        )}
      </nav>
    </div>
  );

  return isMobile ? renderMobileNav() : renderDesktopNav();
};

export default Navbar;